import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ClipboardDocumentIcon,
  KeyIcon,
  ShareIcon,
  TrashIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import {
  createProjectShareKey,
  getProjectShareKeys,
  getProjectShares,
  removeProjectShare,
  revokeProjectShareKey,
} from '../../utils/supabaseClient';

const formatDate = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const getKeyStatus = (key) => {
  if (key.revoked_at) return { label: 'Revoked', className: 'bg-gray-100 text-gray-600' };
  if (key.redeemed_at) return { label: 'Redeemed', className: 'bg-green-100 text-green-700' };
  if (new Date(key.expires_at) <= new Date()) return { label: 'Expired', className: 'bg-amber-100 text-amber-700' };
  return { label: 'Active', className: 'bg-blue-100 text-blue-700' };
};

export default function ProjectSharing({ project }) {
  const [shares, setShares] = useState([]);
  const [keys, setKeys] = useState([]);
  const [newKey, setNewKey] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const canManageShares = project?.project_access_role === 'owner' && project?.id;

  const activeKeys = useMemo(() => keys.filter((key) => {
    return !key.revoked_at && !key.redeemed_at && new Date(key.expires_at) > new Date();
  }), [keys]);

  const loadSharing = useCallback(async () => {
    if (!canManageShares) return;

    try {
      setLoading(true);
      setError('');
      const [shareRows, keyRows] = await Promise.all([
        getProjectShares(project.id),
        getProjectShareKeys(project.id),
      ]);
      setShares(shareRows);
      setKeys(keyRows);
    } catch (err) {
      setError(err.message || 'Failed to load project sharing');
    } finally {
      setLoading(false);
    }
  }, [canManageShares, project?.id]);

  useEffect(() => {
    loadSharing();
  }, [loadSharing]);

  if (!canManageShares) {
    return (
      <div className="bg-white border-y border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center">
            <ShareIcon className="h-5 w-5 text-gray-500 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">Project Sharing</h3>
          </div>
          <p className="text-sm text-gray-500 mt-0.5">
            Sharing is managed by the contractor organization that owns this project.
          </p>
        </div>
        <div className="px-6 py-10">
          <div className="max-w-xl border border-gray-200 bg-gray-50 rounded-sm p-5">
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-sm bg-white border border-gray-200 flex items-center justify-center flex-shrink-0">
                <ShareIcon className="h-5 w-5 text-gray-500" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">
                  This project is shared with your organization as {project?.project_access_role || 'read-only'}.
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Your organization can access the project from Shared with me. Share keys and DOT access are controlled by the project owner.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleCreateKey = async () => {
    try {
      setBusy(true);
      setError('');
      const created = await createProjectShareKey(project.id, 7);
      setNewKey(created);
      try {
        await navigator.clipboard.writeText(created.key);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {
        setCopied(false);
      }
      await loadSharing();
    } catch (err) {
      setError(err.message || 'Failed to create share key');
    } finally {
      setBusy(false);
    }
  };

  const handleCopyKey = async () => {
    if (!newKey?.key) return;
    try {
      await navigator.clipboard.writeText(newKey.key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      setError(err.message || 'Failed to copy key');
    }
  };

  const handleRevokeKey = async (keyId) => {
    try {
      setBusy(true);
      setError('');
      await revokeProjectShareKey(keyId);
      await loadSharing();
    } catch (err) {
      setError(err.message || 'Failed to revoke key');
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveShare = async (shareId) => {
    try {
      setBusy(true);
      setError('');
      await removeProjectShare(shareId);
      await loadSharing();
    } catch (err) {
      setError(err.message || 'Failed to remove DOT access');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border-y border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center">
              <ShareIcon className="h-5 w-5 text-gray-500 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">Project Sharing</h3>
            </div>
            <p className="text-sm text-gray-500 mt-0.5">
              Generate a 7-character key to share this project with a DOT organization.
            </p>
          </div>
          <button
            type="button"
            onClick={handleCreateKey}
            disabled={busy}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded-md"
          >
            <KeyIcon className="h-4 w-4 mr-2" />
            Generate Key
          </button>
        </div>
      </div>

      <div className="px-6 py-6 space-y-6">
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-sm text-sm text-red-700">
            {error}
          </div>
        )}

        {newKey && (
          <div className="border border-blue-200 bg-blue-50 rounded-sm p-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-blue-900">New DOT share key</p>
                <p className="text-xs text-blue-700 mt-1">
                  This key is shown once. It expires {formatDate(newKey.expires_at)}.
                </p>
              </div>
              <button
                type="button"
                onClick={handleCopyKey}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium bg-white border border-blue-200 text-blue-700 rounded-sm hover:bg-blue-100"
              >
                <ClipboardDocumentIcon className="h-4 w-4 mr-2" />
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            <div className="mt-3 font-mono text-3xl tracking-[0.25em] text-blue-950">
              {newKey.key}
            </div>
          </div>
        )}

        {loading ? (
          <div className="py-4 text-sm text-gray-500">Loading sharing...</div>
        ) : (
          <>
            <section>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-gray-900">Active DOT Access</h4>
                <span className="text-xs text-gray-500">{shares.length} organization{shares.length === 1 ? '' : 's'}</span>
              </div>
              {shares.length === 0 ? (
                <div className="text-sm text-gray-500 border border-gray-200 rounded-sm px-3 py-3">
                  No DOT organizations have access yet.
                </div>
              ) : (
                <div className="border border-gray-200 rounded-sm divide-y divide-gray-200">
                  {shares.map((share) => (
                    <div key={share.id} className="px-3 py-3 flex items-center justify-between gap-4">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {share.companies?.name || 'DOT organization'}
                        </p>
                        <p className="text-xs text-gray-500">
                          {share.role} · Shared {formatDate(share.created_at)}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveShare(share.id)}
                        disabled={busy}
                        className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 rounded-sm disabled:opacity-50"
                      >
                        <TrashIcon className="h-4 w-4 mr-1" />
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-gray-900">Share Keys</h4>
                <span className="text-xs text-gray-500">{activeKeys.length} active</span>
              </div>
              {keys.length === 0 ? (
                <div className="text-sm text-gray-500 border border-gray-200 rounded-sm px-3 py-3">
                  No keys have been generated.
                </div>
              ) : (
                <div className="border border-gray-200 rounded-sm divide-y divide-gray-200">
                  {keys.map((key) => {
                    const status = getKeyStatus(key);
                    const canRevoke = status.label === 'Active';
                    return (
                      <div key={key.id} className="px-3 py-3 flex items-center justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${status.className}`}>
                              {status.label}
                            </span>
                            <span className="text-xs text-gray-500">dot_admin</span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Created {formatDate(key.created_at)} · Expires {formatDate(key.expires_at)}
                          </p>
                        </div>
                        {canRevoke && (
                          <button
                            type="button"
                            onClick={() => handleRevokeKey(key.id)}
                            disabled={busy}
                            className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 rounded-sm disabled:opacity-50"
                          >
                            <XCircleIcon className="h-4 w-4 mr-1" />
                            Revoke
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}
